import {
  Chart as ChartJS,
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend,
} from "chart.js";
import { Line } from "react-chartjs-2";
import moment from "moment";
import './index.scss';

ChartJS.register(
  CategoryScale,
  LinearScale,
  PointElement,
  LineElement,
  Tooltip,
  Legend
);

const LabLineChart = ({
  graphics,
  onPointClick,
  fluxgraphMonthAmount,
}) => {
  const colors = [
    { line: 'rgba(146, 133, 249, 1)', point: 'rgba(146, 133, 249, 1)', bg: 'rgba(146, 133, 249, 0.6)' },
    { line: 'rgba(18, 206, 112, 1)', point: 'rgba(18, 206, 112, 1)', bg: 'rgba(132, 244, 188, 0.6)' },
    { line: 'rgba(255, 122, 122, 1)', point: 'rgba(255, 122, 122, 1)', bg: 'rgba(255, 171, 171, 0.6)' },
    { line: 'rgba(205, 91, 232, 1)', point: 'rgba(205, 91, 232, 1)', bg: 'rgba(205, 91, 232, 0.6)' },
    { line: 'rgba(138, 173, 11, 1)', point: 'rgba(138, 173, 11, 1)', bg: 'rgba(199, 242, 43, 0.6)' },
    { line: 'rgba(244, 69, 90, 1)', point: 'rgba(244, 69, 90, 1)', bg: 'rgba(244, 69, 90, 0.6)' },
    { line: 'rgba(20, 173, 204, 1)', point: 'rgba(20, 173, 204, 1)', bg: 'rgba(20, 173, 204, 0.6)' },
    { line: 'rgba(203, 179, 1, 1)', point: 'rgba(203, 179, 1, 1)', bg: 'rgba(254, 224, 3, 0.6)' },
    { line: 'rgba(255, 148, 77, 1)', point: 'rgba(255, 148, 77, 1)', bg: 'rgba(255, 179, 128, 0.6)' },
    { line: 'rgba(0, 204, 146, 1)', point: 'rgba(0, 204, 146, 1)', bg: 'rgba(0, 204, 146, 0.4)' },
    { line: 'rgba(0, 94, 184, 1)', point: 'rgba(0, 94, 184, 1)', bg: 'rgba(0, 94, 184, 0.6)' },
    { line: 'rgba(232, 91, 171, 1)', point: 'rgba(232, 91, 171, 1)', bg: 'rgba(232, 91, 171, 0.6)' },
  ];

  const validData = graphics.filter(planning => 
    Array.isArray(planning.fluxgraph) && planning.fluxgraph.length > 0
  );

  const firstMonths = validData.length > 0 
    ? validData[0].fluxgraph.slice(0, fluxgraphMonthAmount).map(item => item.mes)
    : [];

  const datasets = validData.map((planning, index) => {
    const color = colors[index % colors.length];
    const monthsData = planning.fluxgraph.slice(0, fluxgraphMonthAmount);
    
    return {
      label: planning.nome,
      data: monthsData.map(item => parseFloat(item.capacidadeMensal.toFixed(2))),
      borderColor: color.line,
      backgroundColor: color.point,
      pointBackgroundColor: color.point,
      pointBorderColor: color.line,
      pointBorderWidth: 0,
      pointRadius: 5,
      pointHoverRadius: 7,
      pointHoverBorderWidth: 0,
      tension: 0,
      borderWidth: 2,
    };
  });

  const chartData = {
    labels: firstMonths.map(month => {
      const [year, monthNum] = month.split('-');
      const monthNames = ['Jan', 'Fev', 'Mar', 'Abr', 'Mai', 'Jun', 'Jul', 'Ago', 'Set', 'Out', 'Nov', 'Dez'];
      return `${monthNames[parseInt(monthNum) - 1]}/${year.slice(2)}`;
    }),
    datasets,
  };

  const options = {
    responsive: true,
    maintainAspectRatio: false,
    interaction: {
      mode: 'point',
      intersect: true,
    },
    onHover: (event, activeElements) => {
      event.native.target.style.cursor = activeElements.length > 0 ? 'pointer' : 'default';
    },
    onClick: (event, elements) => {
      if (elements.length > 0 && onPointClick) {
        const element = elements[0];
        const datasetIndex = element.datasetIndex;
        const dateIndex = element.index;
        const planning = validData[datasetIndex];
        const month = planning.fluxgraph[dateIndex]?.mes;
        onPointClick(planning?.id, month);
      }
    },
    scales: {
      y: {
        beginAtZero: true,
        grid: {
          color: "rgba(0,0,0,0.06)",
        },
        ticks: {
          font: {
            size: 11,
            family: 'Figtree',
          },
          callback: function(value) {
            if (value >= 1000) {
              return `R$ ${(value / 1000).toFixed(0)}k`;
            }
            return `R$ ${value.toLocaleString('pt-BR')}`;
          },
        },
      },
      x: {
        grid: {
          display: true,
          color: "rgba(0,0,0,0.06)",
        },
        ticks: {
          font: { 
            size: 11,
            family: 'Figtree',
          },
        }
      },
    },
    plugins: {
      legend: {
        display: true,
        position: 'bottom',
        labels: {
          usePointStyle: true,
          pointStyle: 'circle',
          padding: 15,
          font: {
            size: 11,
            family: 'Figtree',
          },
          color: '#000',
        },
        onHover: (event) => {
          event.native.target.style.cursor = 'pointer';
        },
        onLeave: (event) => {
          event.native.target.style.cursor = 'default';
        },
        onClick: (e, legendItem) => {
          if (onPointClick) {
            const datasetIndex = legendItem.datasetIndex;
            const planning = validData[datasetIndex];
            const currentMonth = moment().format('YYYY-MM');
            onPointClick(planning?.id, currentMonth);
          }
        }
      },
      tooltip: {
        callbacks: {
          label: (context) => {
            const value = context.raw;
            return `${context.dataset.label}: R$ ${value?.toLocaleString('pt-BR', {
              minimumFractionDigits: 2,
              maximumFractionDigits: 2,
            })}`;
          },
        },
      },
    },
  };

  if (validData.length === 0) {
    return (
      <div className="lab-line-chart-container">
        <p>Nenhum dado de fluxo de caixa disponível.</p>
      </div>
    );
  }

  return (
    <div className="lab-line-chart-container">
      <div className="chart-wrapper line">
        <Line data={chartData} options={options} />
      </div>
    </div>
  );
};

export default LabLineChart;
